<template>
    <base-dialog :value="dialog" :width="420" :title="habit ? habitName(habit) : ''" @input="val => { if (!val) hide() }"
        @close="hide">
        <div v-if="habit" class="hr-body">
            <div class="hr-value">
                <span class="hr-big">{{ formatHabitNumber(current) }}</span>
                <span class="hr-goal">/ {{ formatHabitNumber(goal) }} {{ unitLabel }}</span>
            </div>
            <v-progress-linear :value="percent" height="6" rounded color="accent" background-color="outline" />
            <div class="hr-quick">
                <button v-for="step in quickSteps" :key="step" type="button" class="hr-chip" @click="add(step)">
                    +{{ formatHabitNumber(step) }} {{ unitLabel }}
                </button>
                <button type="button" class="hr-chip hr-chip--full" @click="current = goal">{{ $t('habits.complete_goal') }}</button>
            </div>
            <div>
                <label class="hr-label">{{ $t('habits.exact_value') }}</label>
                <input class="hr-input" :value="text" inputmode="decimal" @input="onInput($event.target.value)" />
            </div>
            <button v-if="current > 0" type="button" class="hr-clear" @click="current = 0; text = ''">{{ $t('habits.clear_value') }}</button>
        </div>
        <template v-slot:footer>
            <dialog-actions :confirm-label="$t('common.save')" @cancel="hide" @confirm="save" />
        </template>
    </base-dialog>
</template>

<script>
import BaseDialog from '@/components/common/BaseDialog'
import DialogActions from '@/components/common/DialogActions'
import habitsMixin from '@/mixins/habitsMixin'
import { QUICK_ADD, habitGoal } from '@/utils/Habits'

export default {
    name: 'HabitRegisterDialog',
    mixins: [habitsMixin],
    components: { BaseDialog, DialogActions },
    data() {
        return {
            dialog: false,
            habit: null,
            goals: {},
            current: 0,
            text: '',
        }
    },
    computed: {
        goal(vm = this) {
            return vm.habit ? habitGoal(vm.habit, vm.goals) || 0 : 0
        },
        unitLabel(vm = this) {
            return vm.habit ? vm.habitUnitLabel(vm.habit.unit) : ''
        },
        percent(vm = this) {
            return vm.goal ? Math.min(100, (vm.current / vm.goal) * 100) : 0
        },
        quickSteps(vm = this) {
            if (!vm.habit) return []
            if (QUICK_ADD[vm.habit.unit]) return QUICK_ADD[vm.habit.unit]
            const base = vm.goal || 1
            return [...new Set([1, Math.max(1, Math.round(base / 4)), Math.max(1, Math.round(base / 2))])]
        },
    },
    methods: {
        show(habit, value, goals) {
            const vm = this
            vm.habit = habit
            vm.goals = goals || {}
            vm.current = Number(value) || 0
            vm.text = vm.current ? String(vm.current).replace('.', ',') : ''
            vm.dialog = true
        },
        hide() {
            this.dialog = false
        },
        add(step) {
            const vm = this
            vm.current = Math.round((vm.current + step) * 100) / 100
            vm.text = String(vm.current).replace('.', ',')
        },
        onInput(raw) {
            const vm = this
            vm.text = raw
            const value = Number(String(raw).replace(',', '.'))
            vm.current = Number.isFinite(value) && value > 0 ? value : 0
        },
        save() {
            const vm = this
            vm.$emit('save', { habit: vm.habit, value: vm.current > 0 ? vm.current : null })
            vm.hide()
        },
    },
}
</script>

<style scoped>
.hr-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.hr-value {
    display: flex;
    align-items: baseline;
    gap: 6px;
}

.hr-big {
    font-size: 34px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
}

.hr-goal {
    font-size: 15px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.hr-quick {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.hr-chip {
    height: 36px;
    padding: 0 14px;
    border-radius: 18px;
    border: 1px solid var(--v-outline2-base);
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}

.hr-chip--full {
    border-color: var(--v-primary-base);
    color: var(--v-primary-base);
}

.theme--light .hr-chip--full {
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.hr-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: var(--v-txtSecondary-base);
    margin-bottom: 6px;
}

.hr-input {
    width: 100%;
    height: 44px;
    padding: 0 12px;
    border-radius: 10px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
    font-size: 16px;
    outline: none;
}

.hr-input:focus {
    border-color: var(--v-accent-base);
}

.hr-clear {
    align-self: flex-start;
    background: none;
    border: none;
    padding: 0;
    font-size: 12px;
    color: var(--v-error-base);
    cursor: pointer;
}
</style>
